import {
  addDays,
  endOfQuarter,
  format,
  startOfDay,
  startOfQuarter,
  subDays,
} from 'date-fns';
import {
  getPeriodEndAt,
  getQuarterStartAt,
  getWeekStartAt,
  getYearStartAt,
} from './datetime';

export function formatDayPart(at: Date) {
  return format(at, 'EEEE, MMM d');
}

export function formatRelativeDay(now: Date, targetDayStartAt: Date): string {
  const targetDayStartMs = targetDayStartAt.getTime();

  const todayStartMs = startOfDay(now).getTime();
  const yesterdayStartMs = startOfDay(subDays(now, 1)).getTime();
  const tomorrowStartMs = startOfDay(addDays(now, 1)).getTime();

  if (targetDayStartMs === todayStartMs) {
    return 'Today';
  } else if (targetDayStartMs === yesterdayStartMs) {
    return 'Yesterday';
  } else if (targetDayStartMs === tomorrowStartMs) {
    return 'Tomorrow';
  }

  return format(targetDayStartAt, 'EEEE');
}

export function formatRelativePreviousDay(
  now: Date,
  targetDayStartAt: Date,
): string {
  const todayStartMs = startOfDay(now).getTime();
  const yesterdayStartMs = startOfDay(subDays(now, 1)).getTime();
  const targetMs = targetDayStartAt.getTime();

  if (targetMs === yesterdayStartMs) {
    return 'Yesterday';
  }
  if (targetMs === todayStartMs) {
    return 'Today';
  }
  return 'Previous day';
}

export function formatWeekdayMonthDayYear(dayStartAt: Date): string {
  return format(dayStartAt, 'EEEE, MMM d, yyyy');
}

export function formatMonthDayYear(dayStartAt: Date): string {
  return format(dayStartAt, 'MMM d, yyyy');
}

export function formatWeekPart(at: Date) {
  const weekStartAt = getWeekStartAt(at);
  return format(weekStartAt, "'Week of' MMM d");
}

export function formatRelativeWeek(now: Date, targetWeekStartAt: Date): string {
  const targetWeekStartMs = targetWeekStartAt.getTime();

  const thisWeekStartAt = getWeekStartAt(now);
  const thisWeekStartMs = thisWeekStartAt.getTime();
  const lastWeekStartMs = getWeekStartAt(subDays(thisWeekStartAt, 1)).getTime();
  const nextWeekStartMs = addDays(thisWeekStartAt, 7).getTime();

  if (targetWeekStartMs === thisWeekStartMs) {
    return 'This week';
  } else if (targetWeekStartMs === lastWeekStartMs) {
    return 'Last week';
  } else if (targetWeekStartMs === nextWeekStartMs) {
    return 'Next week';
  }

  return `Week of ${format(targetWeekStartAt, 'MMMM do')}`;
}

export function formatRelativePreviousWeek(
  now: Date,
  targetWeekStartAt: Date,
): string {
  const thisWeekStartAt = getWeekStartAt(now);
  const lastWeekStartMs = getWeekStartAt(subDays(thisWeekStartAt, 1)).getTime();
  const targetMs = targetWeekStartAt.getTime();

  if (targetMs === lastWeekStartMs) {
    return 'Last week';
  }
  if (targetMs === thisWeekStartAt.getTime()) {
    return 'This week';
  }
  return 'Previous week';
}

export function formatWeekRange(startAt: Date) {
  // startAt must be at midnight on a Monday for week period boundaries
  if (
    startAt.getHours() !== 0 ||
    startAt.getMinutes() !== 0 ||
    startAt.getSeconds() !== 0 ||
    startAt.getDay() !== 1 // Monday is day 1 (Sunday is 0)
  ) {
    throw new Error('Start date must be at midnight on a Monday');
  }

  const endAt = getPeriodEndAt(startAt, 'week');

  // End date represents the start of the next week, so subtract 1 day to get the actual last day
  const actualEndAt = subDays(endAt, 1);

  const startYear = format(startAt, 'yyyy');
  const endYear = format(actualEndAt, 'yyyy');
  const startMonth = format(startAt, 'MMM');
  const endMonth = format(actualEndAt, 'MMM');

  if (startYear !== endYear) {
    return `${format(startAt, 'MMM d, yyyy')} - ${format(actualEndAt, 'MMM d, yyyy')}`;
  }

  if (startMonth !== endMonth) {
    return `${format(startAt, 'MMM d')} - ${format(actualEndAt, 'MMM d')}, ${startYear}`;
  }

  return `${startMonth} ${format(startAt, 'd')} - ${format(actualEndAt, 'd')}, ${startYear}`;
}

export function formatQuarterPart(at: Date) {
  const quarterStartAt = getQuarterStartAt(at);
  return format(quarterStartAt, 'QQQ');
}

export function formatRelativeQuarter(now: Date, targetQuarterStartAt: Date) {
  const thisQuarterStartAt = getQuarterStartAt(now);
  const lastQuarterStartAt = getQuarterStartAt(subDays(thisQuarterStartAt, 1));
  const nextQuarterStartAt = getQuarterStartAt(
    getPeriodEndAt(thisQuarterStartAt, 'quarter'),
  );

  const targetMs = targetQuarterStartAt.getTime();
  if (targetMs === thisQuarterStartAt.getTime()) {
    return 'This quarter';
  }
  if (targetMs === lastQuarterStartAt.getTime()) {
    return 'Last quarter';
  }
  if (targetMs === nextQuarterStartAt.getTime()) {
    return 'Next quarter';
  }
  return format(targetQuarterStartAt, 'QQQ yyyy');
}

export function formatRelativePreviousQuarter(
  now: Date,
  targetQuarterStartAt: Date,
): string {
  const thisQuarterStartAt = getQuarterStartAt(now);
  const lastQuarterStartAt = getQuarterStartAt(subDays(thisQuarterStartAt, 1));
  const targetMs = targetQuarterStartAt.getTime();

  if (targetMs === lastQuarterStartAt.getTime()) {
    return 'Last quarter';
  }
  if (targetMs === thisQuarterStartAt.getTime()) {
    return 'This quarter';
  }
  return 'Previous quarter';
}

export function formatQuarterRange(startAt: Date) {
  // Displayed range uses calendar quarter boundaries (e.g. Apr 1–Jun 30)
  // rather than the Monday-aligned planning window — intentional UX trade-off.
  const calendarStart = startOfQuarter(startAt);
  const calendarEnd = endOfQuarter(startAt);
  const year = format(calendarStart, 'yyyy');
  return `${format(calendarStart, 'MMM d')} - ${format(calendarEnd, 'MMM d')}, ${year}`;
}

export function formatYearPart(at: Date) {
  return format(at, 'yyyy');
}

export function formatRelativeYear(now: Date, targetYearStartAt: Date): string {
  const thisYear = now.getFullYear();
  const thisYearStartAt = getYearStartAt(now);
  const lastYearStartAt = getYearStartAt(new Date(thisYear - 1, 0, 15));
  const nextYearStartAt = getYearStartAt(new Date(thisYear + 1, 0, 15));

  const targetMs = targetYearStartAt.getTime();
  if (targetMs === thisYearStartAt.getTime()) {
    return 'This year';
  }
  if (targetMs === lastYearStartAt.getTime()) {
    return 'Last year';
  }
  if (targetMs === nextYearStartAt.getTime()) {
    return 'Next year';
  }

  return format(targetYearStartAt, 'yyyy');
}

export function formatRelativePreviousYear(
  now: Date,
  targetYearStartAt: Date,
): string {
  const thisYearStartAt = getYearStartAt(now);
  const lastYearStartAt = getYearStartAt(subDays(thisYearStartAt, 1));
  const targetMs = targetYearStartAt.getTime();

  if (targetMs === lastYearStartAt.getTime()) {
    return 'Last year';
  }
  if (targetMs === thisYearStartAt.getTime()) {
    return 'This year';
  }
  return 'Previous year';
}

export function formatYearRange(startAt: Date): string {
  // Displayed range uses calendar year boundaries (Jan 1–Dec 31)
  // rather than the Monday-aligned planning window — intentional UX trade-off.
  const year = startAt.getFullYear();
  const calendarStart = new Date(year, 0, 1);
  const calendarEnd = new Date(year, 11, 31);
  return `${format(calendarStart, 'MMM d')} - ${format(calendarEnd, 'MMM d')}, ${format(calendarStart, 'yyyy')}`;
}
